import { useRef, useState } from "react";
import { Button as AntDButton } from "antd";
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Stack,
  TextField,
  Typography,
} from "components";
import { PatientInput } from "types";
import styles from "./PatientForm.module.css";
import UploadFile from "components/Upload/Upload";
import DefaultModal from "components/Modal/DefaultModal";

interface PatientFormProps {
  patient: PatientInput;
  onChange(patient: PatientInput): void;
  onNext?(): void;
  onPatientFilesUploadChange?(file: any): void;
}

const PatientForm: React.FC<PatientFormProps> = ({
  patient,
  onChange,
  onNext,
  onPatientFilesUploadChange,
}) => {
  const ref = useRef<HTMLFormElement>(null);
  const {
    type,
    firstName,
    middleName,
    lastName,
    gender,
    birthday,
    relation,
    notes,
    agreement,
    patientDataSharingAgreement,
    uploadPatientFiles,
    ...concerns
  } = patient;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleFormChange = () => {
    if (ref.current) {
      const formData = new FormData(ref.current);
      const newPatient = Object.fromEntries(formData);
      onChange({
        type: patient.type,
        uploadPatientFiles: patient.uploadPatientFiles,
        ...newPatient,
      });
    }
  };

  const handleUploadPatientFiles = (info: any) => {
    if (info.file) {
      const newFileList = info.fileList.map((file: any) => {
        return file.originFileObj;
      });
      onPatientFilesUploadChange?.(newFileList);
    }
  };

  return (
    <Stack direction="column" placeItems="flex-end" gap={2}>
      <Stack
        gap={2}
        direction="column"
        style={{ width: "80%", placeSelf: "center" }}
      >
        <Typography>* A few questions about the patient</Typography>
        <form ref={ref} onChange={handleFormChange}>
          <Stack direction="column" gap={2}>
            <Stack gap={2}>
              <TextField
                defaultValue={firstName}
                name="firstName"
                label="First name"
                placeholder="First name"
                readOnly={type === "self"}
              />
              <TextField
                defaultValue={middleName}
                name="middleName"
                label="Middle name"
                placeholder="Middle name"
                readOnly={type === "self"}
              />
            </Stack>
            <Stack gap={2}>
              <TextField
                defaultValue={lastName}
                name="lastName"
                label="Last name"
                placeholder="Last name"
                readOnly={type === "self"}
                style={{ flex: 1 }}
              />
              <CheckboxGroup
                name="gender"
                type="radio"
                label="Gender"
                options={["male", "female"]}
                defaults={[gender]}
                readOnly={type === "self"}
                style={{ flex: 1 }}
              />
            </Stack>
            <Stack gap={2}>
              <TextField
                defaultValue={birthday}
                name="birthday"
                label="Birthday"
                type="date"
                readOnly={type === "self"}
                style={{ flex: 1 }}
              />
              <div style={{ flex: 1 }} />
            </Stack>
            <Stack direction="column" gap={2}>
              <TextField
                defaultValue={relation}
                name="relation"
                label="What is your relation to the patient?"
                placeholder="Relation"
                readOnly={type !== "others"}
                {...(type === "self" && { type: "hidden" })}
              />
              <CheckboxGroup
                name="concern"
                label="* What are the reasons for the visit?"
                options={["cough", "fever", "cold", "stomach pains", "fatigue"]}
                defaults={Object.values(concerns)}
              />
              <TextField
                defaultValue={notes}
                name="notes"
                label="Others"
                placeholder="Please write symptoms, medication taken, and other relevant information"
                multiline
              />
              <UploadFile
                allowedTypes={[".png", ".jpeg", ".pdf"]}
                onChange={handleUploadPatientFiles}
              />
              <Stack gap={0.5}>
                <Checkbox
                  name="agreement"
                  value="yes"
                  defaultChecked={agreement === "yes"}
                >
                  <Typography>By continuing, I agree to the</Typography>
                </Checkbox>
                <a
                  className={styles.link}
                  href="https://www.mwell.com.ph/terms-and-conditions"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Typography>Terms and Conditions</Typography>
                </a>
              </Stack>
              <Stack gap={0.5}>
                <Checkbox
                  name="patientDataSharingAgreement"
                  value="yes"
                  defaultChecked={patientDataSharingAgreement === "yes"}
                >
                  <Typography>By continuing, I agree to the</Typography>
                </Checkbox>
                <AntDButton
                  className={styles.link}
                  onClick={() => setIsModalOpen(true)}
                >
                  <Typography>Data Sharing and Agreement</Typography>
                </AntDButton>
                <DefaultModal
                  title={"AGREEMENT"}
                  theme="primary"
                  isShow={isModalOpen}
                  children={
                    <p>
                      To provide you an accurate medical assessment and course
                      of treatment, your doctor will need access to your
                      existing patient records in the mWell App. By clicking
                      "accept" you agree to the sharing of your patient records
                      to your doctor for the purpose of providing you medical
                      advice during your next consultation.
                    </p>
                  }
                  onCancel={() => setIsModalOpen(false)}
                />
              </Stack>
            </Stack>
          </Stack>
        </form>
      </Stack>
      <Button
        filled
        onClick={onNext}
        disabled={!onNext}
        iconEnd="arrow_forward"
      >
        Continue
      </Button>
    </Stack>
  );
};

export { PatientForm };
